import { useEffect, useState, useRef } from 'react'
import { useFetchAllUsersLazyQuery, User, UserRole, useSearchUserLazyQuery } from '../../generated'
import { DEFAULT_USER_ROLES, GRAPHQL_QUERY_POLICY, GRAPHQL_QUERY_POLICY_CACHE_FIRST, PAGE_LIMIT } from '../constants'
import { useDebounce } from 'use-debounce'

const useUsers = ({
  customUserRoles,
  page,
  limit
}: {
  customUserRoles?: UserRole[]
  page?: number
  limit?: number
}) => {
  const [allUsers, setAllUsers] = useState<User[]>([]) // Users currently displayed in dropdown
  const [userCache, setUserCache] = useState<Map<string, User>>(new Map()) // Cache of all users ever fetched
  const [userPage, setUserPage] = useState<number>(page ?? 1)
  const [hasMore, setHasMore] = useState(true)
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [debouncedUserSearchTerm] = useDebounce(userSearchTerm, 500)
  const [isSearching, setIsSearching] = useState(false)
  const initialLoadRef = useRef(false)

  const [fetchAllUsers, { loading }] = useFetchAllUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onCompleted(data) {
      const { fetchAllUsers } = data || {}
      const { users, pagination, response } = fetchAllUsers || {}
      if (users && response?.status === 200) {
        const fetchedUsers = users as User[]

        // Update cache with new users
        setUserCache(prevCache => {
          const newCache = new Map(prevCache)
          fetchedUsers.forEach(user => {
            if (user.id) {
              newCache.set(user.id, user)
            }
          })
          return newCache
        })

        // Update displayed users
        setAllUsers(prevUsers => {
          if (userPage === 1 || isSearching) {
            return fetchedUsers
          }
          return [...prevUsers, ...fetchedUsers]
        })
        setHasMore(!(pagination?.totalPages === userPage))
      }
    }
  })

  const [searchUsers, { loading: userSearchLoading }] = useSearchUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted(data) {
      if (data?.searchUser?.users) {
        const users = data.searchUser.users as User[]

        // Update cache with search results
        setUserCache(prevCache => {
          const newCache = new Map(prevCache)
          users.forEach(user => {
            if (user.id) {
              newCache.set(user.id, user)
            }
          })
          return newCache
        })

        // Update displayed users
        setAllUsers(users)
      }
    }
  })

  // Function to get user from cache
  const getUserFromCache = (userId: string): User | undefined => {
    return userCache.get(userId)
  }

  // Initial load
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true
      fetchAllUsers({
        variables: {
          userInput: {
            paginationOptions: {
              limit: limit ?? PAGE_LIMIT,
              page: 1
            },
            roles: customUserRoles ?? DEFAULT_USER_ROLES
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle search
  useEffect(() => {
    if (debouncedUserSearchTerm.trim()) {
      setIsSearching(true)
      setUserPage(1)
      setHasMore(true)
      searchUsers({
        variables: {
          searchUserInput: {
            roles: customUserRoles ?? DEFAULT_USER_ROLES,
            searchTerm: debouncedUserSearchTerm.trim()
          }
        }
      })
    } else if (isSearching) {
      // Only refetch when transitioning from search to no search
      setIsSearching(false)
      setUserPage(1)
      setHasMore(true)
      fetchAllUsers({
        variables: {
          userInput: {
            paginationOptions: {
              limit: limit ?? PAGE_LIMIT,
              page: 1
            },
            roles: customUserRoles ?? DEFAULT_USER_ROLES
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUserSearchTerm])

  // Handle pagination (only when not searching)
  useEffect(() => {
    if (!isSearching && userPage > 1 && hasMore) {
      fetchAllUsers({
        variables: {
          userInput: {
            paginationOptions: {
              limit: limit ?? PAGE_LIMIT,
              page: userPage
            },
            roles: customUserRoles ?? DEFAULT_USER_ROLES
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPage, isSearching, hasMore])

  return {
    allUsers,
    setAllUsers,
    loading: loading || userSearchLoading,
    setUserPage,
    userPage,
    userSearchLoading,
    userSearchTerm,
    setUserSearchTerm,
    getUserFromCache // Export this function to get cached users
  }
}

export default useUsers
